import { has, isEmpty, omit } from 'lodash';
import { isUiModeCode } from 'new/utils/uiMode';
import * as actionTypes from '../../constants/actionTypes';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { isCapsuleCluster } from '../../lib/PipelineUtils';

import Constants from '../../Constants';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
} from '../../constants/connectionCategories';
import { ACTIVATE_SINK, COPY_SINK, DELETE_SINK, PAUSE_SINK } from '../../constants/processTypes';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { getConnectionOrVendorType } from '../../lib/ResourceUtils';
import UIUtils from '../../lib/UIUtils';
import { getCurrentOrg, getCurrentUser } from '../../reducers/selectors';
import { sinkSchema } from '../../schemas';
import { fetchDataSet } from '../dataSets';
import { addProcess, removeProcess } from '../processes';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import flowTypes from 'new/components/flows/types/flowTypes';
import { refreshPipeline } from 'js/actions';
import { cleanObject } from 'new/utils';
import {
  getOrgConfigOverrides,
  applyOrgConfigOverrides,
} from 'new/utils/FeatureUtil/orgConfigOverrides';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { debugResourcePayload, isResourceDebugEnabled } from 'js/lib/ResourcePayloadDebugger';
import { getResourceNameWithFallback } from '../../lib/ResourceNameUtils';
import { normalizeCredentialId } from '../../lib/utils/credentialUtils';

export const loadMyDataSinks =
  (page, sortCriteria, availableFilters, options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.DATA_SINKS],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_sinks';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_sinks/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_SINKS_REQUEST,
          actionTypes.GET_SINKS_SUCCESS,
          actionTypes.GET_SINKS_FAILURE,
        ],
        endpoint,
        schema: [sinkSchema],
        requestMethod,
        aclQueryMode,
        payload,
        requestInfo: { view: SearchFilterViews.DATA_SINKS },
      },
    }).then((response) => {
      return response;
    });
  };

export const loadDataSink =
  (
    id,
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_SINK_REQUEST,
          actionTypes.GET_SINK_SUCCESS,
          actionTypes.GET_SINK_FAILURE,
        ],
        endpoint: `/data_sinks/${id}`,
        schema: sinkSchema,
        requestMethod: GET,
        aclQueryMode,
        ignoreErrors,
        payload: {
          expand: 1,
        },
      },
    });
  };

export const loadDataSinkSearchSuggestions =
  (filters, availableFilters) => (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const { sortKey, sortOrder } =
      Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_SINK_REQUEST,
          actionTypes.SEARCH_SINK_SUCCESS,
          actionTypes.SEARCH_SINK_FAILURE,
        ],
        endpoint: `/data_sinks/search?${params}`,
        schema: [sinkSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.DATA_SINKS },
      },
    });
  };

export const loadDataSinkWithDataSet = (id) => (dispatch) => {
  return dispatch(loadDataSink(id)).then((response) => {
    const {
      result,
      entities: { sinks },
    } = response;
    const dataSetId = sinks[result].data_set_id;

    if (dataSetId) {
      return dispatch(fetchDataSet(dataSetId));
    }
  });
};

export const addDataSink =
  (sinkType, info = null, credentialId = null) =>
  (dispatch, getState) => {
    const {
      entities: { credentials = {} } = {},
      paging: { metaInfoById = {}, sinkConfigById },
    } = getState();
    const meta = metaInfoById[sinkType];

    if (
      !info ||
      (!credentialId &&
        !has(info, 'data_map') &&
        meta &&
        meta.connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT)
    ) {
      dispatch({
        type: actionTypes.ADD_SINK_FAILURE,
        error: 'Insufficient destination information',
      });

      return Promise.reject(new Error('Insufficient destination information'));
    }

    let payload = {
      ...info,
      sink_type: sinkType,
    };

    if (has(info, 'sink_config')) {
      // NEX-21470: in code mode the user owns the entire sink_config — do not
      // mutate mapping / tracker / etc. Form-mode behavior unchanged.
      if (!isUiModeCode(info.sink_config) && info.sink_config.mapping) {
        if (info.sink_config.mapping.mode === 'auto') {
          const mappingPropsToIgnore = ['mapping', 'fields_order'];

          if (UIUtils.headerlessFormats.indexOf(info.sink_config.data_format) > -1) {
            mappingPropsToIgnore.push('header_template');
          }

          info.sink_config.mapping = omit(info.sink_config.mapping, mappingPropsToIgnore);
        }

        if (info.sink_config.mapping.tracker_mode === 'RECORD') {
          if (!info.sink_config.mapping.tracker_name) {
            info.sink_config.mapping.tracker_name = '__nexla_tracker_id';
          }
        } else {
          info.sink_config.mapping.tracker_mode = 'NONE';
          delete info.sink_config.mapping.tracker_name;
        }
      }

      //TODO: temp for NEX-2663
      if (has(info.sink_config, 'name')) {
        delete payload.sink_config['name'];
      }

      if (has(info.sink_config, 'description')) {
        delete payload.sink_config['description'];
      }

      // set template_config for templatized API's
      if (meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API) {
        let templatizedAPIPayload = UIUtils.buildSourceFromTemplatizedAPIConfig(
          info.sink_config,
          sinkConfigById[sinkType],
        );

        templatizedAPIPayload = cleanObject(templatizedAPIPayload);

        payload = {
          ...payload,
          ...templatizedAPIPayload,
          sink_type: meta.connectionType,
        };

        // Mark code-mode origin on whichever config container the upstream
        // builder used. In code mode the payload carries `source_config`; in
        // view mode it carries `template_config`. Either may be absent for
        // partial configs, so guard before writing.
        if (info.sink_config['ui.mode'] === 'code') {
          const container = payload.source_config ?? payload.template_config;
          if (container) container['ui.mode'] = 'code';
        }

        delete payload.sink_config;
      }

      // set script_config for templatized scripts
      if (meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT) {
        const templatizedScriptPayload = UIUtils.buildSourceFromTemplatizedScriptConfig(
          info.sink_config,
          sinkConfigById[sinkType],
        );

        payload = {
          ...payload,
          ...templatizedScriptPayload,
        };
        delete payload.sink_type;
      }
    }

    // TODO: temp hack to remove name/description from sink_config which might
    // have been added by JSON mode
    delete payload.sink_config?.name;
    delete payload.sink_config?.description;

    // NEX-21470: code-mode payloads bypass typecast + org-override injection.
    const isCodeMode = isUiModeCode(info?.sink_config);

    if (!isCodeMode) {
      _typeCastCredentialConfigValues(payload, getState());
    }

    const credentialIdToSend = normalizeCredentialId(credentialId);
    const credential =
      credentialId != null && typeof credentialId === 'object'
        ? credentials[credentialIdToSend] || credentialId
        : credentials[credentialIdToSend];

    payload.name = getResourceNameWithFallback({
      currentName: payload.name,
      connectionType: sinkType,
      metaInfo: meta,
      credential,
      resourceLabel: 'Destination',
    });

    if (
      credentialIdToSend &&
      (!meta || meta.connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT)
    ) {
      payload.data_credentials = credentialIdToSend;
    }
    //check if the sink_config should have any overrides. if so apply them. Be careful not to screw up orgs that should not have overrides.
    const org = getCurrentOrg(getState());
    // flow_type is either present in info or undefined.
    const flowType = info?.flow_type || flowTypes.streaming;

    const orgConfigOverrides = !isCodeMode
      ? getOrgConfigOverrides(org.id, 'sink_config', meta?.connectionCategory, flowType)
      : null;
    if (orgConfigOverrides) {
      payload.sink_config = applyOrgConfigOverrides(payload.sink_config, orgConfigOverrides);
      //TODO: this is temporary logging. Remove this when orgOverrides are well tested.
      console.info('Sink Creation: orgConfigOverrides', orgConfigOverrides);
      console.info('Sink Creation: sink_config', payload.sink_config);
    }

    const dataSets = getState().entities?.dataSets;
    const isCapsule = isCapsuleCluster(dataSets?.[info?.data_set_id]);
    const capsuleSettingsEnabled = getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS');
    const endpoint =
      isCapsule && capsuleSettingsEnabled
        ? `/data_sinks?expand=1&activate_now=true`
        : `/data_sinks?expand=1`;

    // Debug mode: log payload and optionally block API call
    if (isResourceDebugEnabled()) {
      const shouldBlock = debugResourcePayload('CREATE_SINK', payload, {
        endpoint: '/data_sinks',
        resourceType: sinkType,
        connectionCategory: meta?.connectionCategory,
        flowType,
      });

      if (shouldBlock) {
        // Dispatch failure action to trigger normal error handling in components
        dispatch({
          type: actionTypes.ADD_SINK_FAILURE,
          error: 'Blocked by debugger',
        });
        return Promise.resolve({ debuggerBlocked: true });
      }
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ADD_SINK_REQUEST,
          actionTypes.ADD_SINK_SUCCESS,
          actionTypes.ADD_SINK_FAILURE,
        ],
        endpoint,
        schema: sinkSchema,
        requestMethod: POST,
        payload,
      },
    }).catch((error) => {
      console.log(`Error creating destination`);
      console.log(error);
      return Promise.reject(error);
    });
  };

export const copySink =
  (fromSinkId = null) =>
  (dispatch, getState) => {
    if (!fromSinkId) {
      return dispatch({
        type: actionTypes.COPY_SINK_FAILURE,
        error: 'Insufficient sink information',
      });
    }

    const state = getState();
    const {
      entities: { sinks },
    } = state;
    const fromSink = sinks[fromSinkId];

    dispatch(
      addProcess(COPY_SINK, `Duplicating Sink "${fromSink.name}"`, {
        id: fromSinkId,
      }),
    );

    let payload = {
      copy_access_controls: false,
      reuse_data_credentials: true,
      copy_other_data_sinks: false,
    };

    const user = getCurrentUser(state);
    const org = getCurrentOrg(state);
    if (!isEmpty(user) && !isEmpty(org)) {
      payload.owner_id = user.id;
      payload.org_id = org.id;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.COPY_SINK_REQUEST,
          actionTypes.COPY_SINK_SUCCESS,
          actionTypes.COPY_SINK_FAILURE,
        ],
        endpoint: `/data_sinks/${fromSinkId}/copy`,
        schema: sinkSchema,
        requestMethod: POST,
        payload,
      },
    }).finally(() => {
      dispatch(removeProcess(COPY_SINK, { id: fromSinkId }));
    });
  };

function prepareSinkPayload(id, newInfo, getState) {
  let payload = Object.assign({}, newInfo);
  const {
    entities: { sinks = {}, vendors = {}, credentials = {} } = {},
    paging: { metaInfoById = {}, sinkConfigById = {} } = {},
  } = getState();
  const sink = sinks[id];

  const sinkType = sink
    ? getConnectionOrVendorType(sink, vendors, 'sink_type', 'vendor_id')
    : payload.sink_type;

  if (payload.data_credentials != null) {
    payload.data_credentials = normalizeCredentialId(payload.data_credentials);
  }

  if (has(newInfo, 'sink_config')) {
    // NEX-21470: code-mode updates send sink_config verbatim — no mapping
    // rewrite, no replication-flow mapping delete.
    const isCodeMode = isUiModeCode(newInfo.sink_config);

    if (!isCodeMode && newInfo.sink_config.mapping) {
      if (newInfo.sink_config.mapping.mode === 'auto') {
        const mappingPropsToIgnore = ['mapping', 'fields_order'];

        if (UIUtils.headerlessFormats.indexOf(newInfo.sink_config.data_format) > -1) {
          mappingPropsToIgnore.push('header_template');
        }

        newInfo.sink_config.mapping = omit(newInfo.sink_config.mapping, mappingPropsToIgnore);
      }

      if (newInfo.sink_config.mapping.tracker_mode === 'RECORD') {
        if (!newInfo.sink_config.mapping.tracker_name) {
          newInfo.sink_config.mapping.tracker_name = '__nexla_tracker_id';
        }
      } else {
        newInfo.sink_config.mapping.tracker_mode = 'NONE';
        delete newInfo.sink_config.mapping.tracker_name;
      }

      // remove body_template from legacy sinks
      if (sinkType === 'rest' && newInfo.sink_config.mapping.body_template) {
        delete newInfo.sink_config.mapping.body_template;
      }
    }

    if (
      !isCodeMode &&
      newInfo.flowType === flowTypes.replication &&
      newInfo?.sink_config?.mapping
    ) {
      delete newInfo.sink_config.mapping;
    }

    //TODO: temp for NEX-2663
    if (has(newInfo.sink_config, 'name')) {
      delete payload.sink_config['name'];
    }

    if (has(newInfo.sink_config, 'description')) {
      delete payload.sink_config['description'];
    }

    // set template_config for templatized API's
    const meta = metaInfoById[sinkType];

    if (
      meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API &&
      !(newInfo.sink_config.sink_type === 'rest' && newInfo.sink_config.vendor_id)
    ) {
      const templatizedAPIPayload = UIUtils.buildSourceFromTemplatizedAPIConfig(
        newInfo.sink_config,
        sinkConfigById[sinkType],
      );

      payload = {
        ...payload,
        ...templatizedAPIPayload,
        sink_type: meta.connectionType,
      };

      // See create path: in code mode the upstream builder writes
      // source_config (not template_config), so guard against either being
      // absent before stamping the marker.
      if (newInfo.sink_config['ui.mode'] === 'code') {
        const container = payload.source_config ?? payload.template_config;
        if (container) container['ui.mode'] = 'code';
      }

      delete payload.sink_config;
    }

    // set script_config for templatized scripts
    if (meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT) {
      const templatizedScriptPayload = UIUtils.buildSourceFromTemplatizedScriptConfig(
        newInfo.sink_config,
        sinkConfigById[sinkType],
      );

      delete payload.sink_type;
      payload = {
        ...payload,
        ...templatizedScriptPayload,
      };

      if (payload.sink_config) {
        payload.sink_config = {
          ...sink.sink_config,
          ...payload.sink_config,
        };
      }
    }
  }

  // NEX-21470: skip typecast on code-mode updates so user-typed values reach
  // the wire verbatim.
  if (!isUiModeCode(newInfo?.sink_config)) {
    _typeCastCredentialConfigValues(payload, getState());
  }

  // TODO: temp hack to remove name/description from sink_config which might
  // have been added by JSON mode
  delete payload.sink_config?.name;
  delete payload.sink_config?.description;

  if (has(payload, 'name')) {
    const credentialCandidate =
      newInfo.data_credentials ??
      payload.data_credentials ??
      payload.data_credentials_id ??
      sink?.data_credentials ??
      sink?.data_credentials_id;
    const credentialId = normalizeCredentialId(credentialCandidate);
    const credential =
      credentials[credentialId] ||
      (credentialCandidate != null && typeof credentialCandidate === 'object'
        ? credentialCandidate
        : undefined);
    const currentName =
      payload.name != null && String(payload.name).trim() ? payload.name : sink?.name;

    payload.name = getResourceNameWithFallback({
      currentName,
      connectionType: payload.sink_type || sinkType,
      metaInfo: metaInfoById[payload.sink_type] || metaInfoById[sinkType],
      credential,
      resourceLabel: 'Destination',
    });
  }

  return payload;
}

export const updateSinkInfo =
  (id, newInfo = {}) =>
  (dispatch, getState) => {
    const payload = prepareSinkPayload(id, newInfo, getState);

    // Debug mode: log payload and optionally block API call
    if (isResourceDebugEnabled()) {
      const shouldBlock = debugResourcePayload('UPDATE_SINK', payload, {
        endpoint: `/data_sinks/${id}`,
        resourceId: id,
      });

      if (shouldBlock) {
        // Dispatch failure action to trigger normal error handling in components
        dispatch({
          type: actionTypes.UPDATE_SINK_INFO_FAILURE,
          error: 'Blocked by debugger',
        });
        return Promise.resolve({ debuggerBlocked: true });
      }
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_SINK_INFO_REQUEST,
          actionTypes.UPDATE_SINK_INFO_SUCCESS,
          actionTypes.UPDATE_SINK_INFO_FAILURE,
        ],
        endpoint: `/data_sinks/${id}?expand=1`,
        schema: sinkSchema,
        requestMethod: PUT,
        payload,
      },
    });
  };

export const deleteSink =
  (id, progressLabel = null) =>
  (dispatch, getState) => {
    const {
      entities: { sinks },
    } = getState();
    const sink = sinks[id];

    dispatch(
      addProcess(DELETE_SINK, progressLabel || `Deleting Destination "${sink.name}"`, { id }),
    );

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.DELETE_SINK_REQUEST,
          actionTypes.DELETE_SINK_SUCCESS,
          actionTypes.DELETE_SINK_FAILURE,
        ],
        endpoint: `/data_sinks/${id}`,
        schema: sinkSchema,
        requestMethod: DELETE,
        requestInfo: {
          id,
          nodeKey: sink.flow_node_id,
        },
      },
    })
      .then(() => {
        if (sink?.origin_node_id) {
          dispatch(refreshPipeline(sink.origin_node_id));
        }
      })
      .finally(() => {
        dispatch(removeProcess(DELETE_SINK, { id }));
      });
  };

export const activateSink = (id) => (dispatch, getState) => {
  const {
    entities: { sinks },
  } = getState();
  const sink = sinks[id];

  dispatch(
    addProcess(ACTIVATE_SINK, `Activating Destination "${sink.name}"`, {
      id,
    }),
  );

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACTIVATE_SINK_REQUEST,
        actionTypes.UPDATE_SINK_INFO_SUCCESS,
        actionTypes.UPDATE_SINK_INFO_FAILURE,
      ],
      endpoint: `/data_sinks/${id}/activate?expand=1`,
      schema: sinkSchema,
      requestMethod: PUT,
    },
  }).finally(() => {
    dispatch(removeProcess(ACTIVATE_SINK, { id }));
  });
};

export const pauseSink =
  (id, progressLabel = null) =>
  (dispatch, getState) => {
    const {
      entities: { sinks },
    } = getState();
    const sink = sinks[id];

    dispatch(addProcess(PAUSE_SINK, progressLabel || `Pausing Destination "${sink.name}"`, { id }));

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PAUSE_SINK_REQUEST,
          actionTypes.UPDATE_SINK_INFO_SUCCESS,
          actionTypes.UPDATE_SINK_INFO_FAILURE,
        ],
        endpoint: `/data_sinks/${id}/pause?expand=1`,
        schema: sinkSchema,
        requestMethod: PUT,
      },
    }).finally(() => {
      dispatch(removeProcess(PAUSE_SINK, { id }));
    });
  };

const _typeCastCredentialConfigValues = (payload, state) => {
  if (payload.sink_config && payload.sink_type) {
    const {
      paging: { sinkConfigById },
    } = state;
    payload.sink_config = UIUtils.castToFieldType(
      payload.sink_config,
      sinkConfigById[payload.sink_type],
    );
  }
};
